/**
 * American Surgical Authorized-Distributor Announcement & Pre-Order Landing Page
 * Route: /marketplace/american-surgical
 * Linked from: SurgicalCatalog.jsx + homepage email campaign
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, X,
  CheckCircle2, Mail, Phone, Download, Star, Award, Sparkles,
  AlertTriangle, Package, Truck, ArrowRight, Calendar, Building2,
  ShieldCheck, FileText, ShoppingCart, Plus, Minus, ChevronDown, ChevronUp, Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BackToHome from "@/components/BackToHome";
import SiteHeader from "@/components/SiteHeader";
import SEO, { productSchema } from "@/components/SEO";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import PdfFlipbook from "@/components/PdfFlipbook";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { ASC_PRODUCTS } from "@/data/americanSurgicalCatalog";
import { getAscPrice, refreshAscPricing } from "@/data/americanSurgicalPricing";

const CATALOG_URL = "/catalogs/2026-asc-sterilemate-catalog.pdf";
const CATALOG_DOWNLOAD_NAME = "2026-ASC-SterileMate-Catalog.pdf";
const ASC_SOURCE = "American Surgical (Sterile Mate)";

// ----- Single product widget — expandable card with full SKU table ------------
const ProductWidget = ({ product, addItem }) => {
  const [open, setOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [qtyBySku, setQtyBySku] = useState({});  // { "60-08": 5 }

  const getQty = (code) => qtyBySku[code] ?? 1;
  const setQty = (code, n) => setQtyBySku((m) => ({ ...m, [code]: Math.max(1, Math.min(999, n)) }));
  const bumpQty = (code, delta) => setQty(code, getQty(code) + delta);

  const images = [product.img, ...(product.gallery || [])];
  const hasGallery = images.length > 1;

  const next = () => setImgIdx((i) => (i + 1) % images.length);
  const prev = () => setImgIdx((i) => (i - 1 + images.length) % images.length);

  const onAdd = (sku) => {
    const price = getAscPrice(sku.code);
    const qty = getQty(sku.code);
    addItem({
      erp: sku.code,
      name: `${product.name} ${sku.code} — ${sku.a || ""}${sku.b ? " · " + sku.b : ""}`.trim(),
      image: product.img,
      source: ASC_SOURCE,
      unit_price: price?.hosp || null,
      list_price: price?.list || null,
      pack: price?.pack || product.packaging,
    }, qty);
    // reset that row's qty back to 1 for a clean next-add
    setQty(sku.code, 1);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-blue-400 hover:shadow-xl transition-all" data-testid={`asc-widget-${product.id}`}>
      <div className="grid md:grid-cols-[280px_1fr]">
        {/* Image side — carousel */}
        <div className="relative aspect-[4/3] md:aspect-auto bg-slate-50 overflow-hidden group">
          <button
            type="button"
            onClick={() => setLightbox(true)}
            className="block w-full h-full"
            aria-label={`View ${product.name} larger`}
            data-testid={`asc-img-zoom-${product.id}`}
          >
            <img src={images[imgIdx]} alt={`${product.name} — ${imgIdx + 1}`} className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-[1.03]" />
          </button>

          {product.badge && (
            <span className={`absolute top-3 left-3 px-2 py-1 text-[10px] font-black uppercase tracking-wider rounded ${product.badge === "New" ? "bg-emerald-500 text-white" : product.badge === "Best Seller" ? "bg-amber-400 text-slate-900" : "bg-blue-600 text-white"}`}>
              {product.badge}
            </span>
          )}

          {hasGallery && (
            <>
              <button
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-slate-700 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Previous image"
                data-testid={`asc-img-prev-${product.id}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-slate-700 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Next image"
                data-testid={`asc-img-next-${product.id}`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === imgIdx ? "bg-blue-700 w-4" : "bg-slate-300 hover:bg-slate-500"}`}
                    aria-label={`Go to image ${i + 1}`}
                    data-testid={`asc-img-dot-${product.id}-${i}`}
                  />
                ))}
              </div>
              <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-bold rounded bg-slate-900/70 text-white">
                {imgIdx + 1} / {images.length}
              </span>
            </>
          )}
        </div>

        {/* Content side */}
        <div className="p-5 flex flex-col">
          <h3 className="text-xl font-bold text-slate-900">{product.name}</h3>
          <p className="text-xs font-semibold text-blue-700 mb-2">{product.sub}</p>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">{product.blurb}</p>

          <ul className="grid grid-cols-2 gap-x-3 gap-y-1 mb-3">
            {product.bullets.map((b) => (
              <li key={b} className="flex items-start gap-1.5 text-xs text-slate-600">
                <CheckCircle2 className="w-3 h-3 text-blue-500 shrink-0 mt-0.5" /> {b}
              </li>
            ))}
          </ul>

          {hasGallery && (
            <div className="flex gap-1.5 mb-3" data-testid={`asc-thumbs-${product.id}`}>
              {images.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setImgIdx(i)}
                  className={`w-12 h-12 rounded border-2 overflow-hidden bg-slate-50 transition ${i === imgIdx ? "border-blue-600 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"}`}
                  aria-label={`View image ${i + 1}`}
                  data-testid={`asc-thumb-${product.id}-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}

          <p className="text-[11px] text-slate-500 italic mb-3">
            {product.packaging} · Made in {product.madeIn}
          </p>

          <div className="mt-auto flex flex-wrap gap-2">
            <button
              onClick={() => setOpen(!open)}
              className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold transition-colors"
              data-testid={`asc-widget-toggle-${product.id}`}
            >
              {open ? "Hide SKUs" : `View SKUs (${product.skuGroups.reduce((n, g) => n + g.rows.length, 0)})`}
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {product.videoId && (
              <button
                onClick={() => setVideoOpen(!videoOpen)}
                className={`inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-lg text-sm font-bold transition-colors ${videoOpen ? "bg-rose-600 hover:bg-rose-500 text-white border border-rose-700" : "border border-rose-300 text-rose-700 hover:bg-rose-50"}`}
                data-testid={`asc-video-toggle-${product.id}`}
                aria-expanded={videoOpen}
              >
                {videoOpen ? <ChevronUp className="w-4 h-4" /> : <Play className="w-3 h-3 fill-current" />}
                {videoOpen ? "Hide Video" : "Watch Video"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Video embed — lazy-mounted only when toggled */}
      {videoOpen && product.videoId && (
        <div className="border-t border-slate-200 bg-slate-900 p-4 relative" data-testid={`asc-video-${product.id}`}>
          <button
            onClick={() => setVideoOpen(false)}
            className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-3 h-8 rounded-full bg-white/90 hover:bg-white text-slate-900 text-xs font-bold shadow"
            data-testid={`asc-video-close-${product.id}`}
          >
            <X className="w-3 h-3" /> Close
          </button>
          <div className="relative w-full max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src={`https://www.youtube.com/embed/${product.videoId}?autoplay=1&rel=0`}
              title={`${product.name} — product video`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(false)}
          data-testid={`asc-lightbox-${product.id}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          {hasGallery && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <img
            src={images[imgIdx]}
            alt={product.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Expanded SKU tables */}
      {open && (
        <div className="border-t border-slate-200 bg-slate-50 p-5 space-y-5" data-testid={`asc-widget-skus-${product.id}`}>
          {product.skuGroups.map((group) => (
            <div key={group.label}>
              <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">{group.label}</p>
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      {group.columns.map((c) => (
                        <th key={c} className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider">{c}</th>
                      ))}
                      <th className="px-3 py-2 text-right font-semibold uppercase text-[10px] tracking-wider">2026 Price</th>
                      <th className="px-3 py-2 text-center font-semibold uppercase text-[10px] tracking-wider">Qty</th>
                      <th className="px-3 py-2 text-right font-semibold uppercase text-[10px] tracking-wider">Quote Cart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {group.rows.map((row) => {
                      const price = getAscPrice(row.code);
                      const qty = getQty(row.code);
                      return (
                        <tr key={row.code} className="hover:bg-blue-50/50">
                          <td className="px-3 py-2 font-mono font-semibold text-slate-900">{row.code}</td>
                          <td className="px-3 py-2 text-slate-700">{row.a}</td>
                          {group.columns.length === 3 && <td className="px-3 py-2 text-slate-700">{row.b}</td>}
                          <td className="px-3 py-2 text-right font-bold text-emerald-700 tabular-nums" data-testid={`asc-hosp-price-${row.code}`}>
                            {price?.hosp ? `$${price.hosp.toFixed(2)}` : <span className="text-slate-300">quoted</span>}
                          </td>
                          <td className="px-3 py-2">
                            <div className="inline-flex items-center justify-center gap-0 rounded-md border border-slate-300 bg-white overflow-hidden">
                              <button
                                type="button"
                                onClick={() => bumpQty(row.code, -1)}
                                className="w-6 h-7 flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:text-slate-300"
                                aria-label={`Decrease quantity for ${row.code}`}
                                data-testid={`asc-qty-dec-${row.code}`}
                                disabled={qty <= 1}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                min={1}
                                max={999}
                                value={qty}
                                onChange={(e) => setQty(row.code, parseInt(e.target.value || "1", 10))}
                                onFocus={(e) => e.target.select()}
                                className="w-10 h-7 text-center text-sm font-semibold text-slate-900 bg-transparent border-x border-slate-200 focus:outline-none focus:bg-amber-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                data-testid={`asc-qty-input-${row.code}`}
                                aria-label={`Quantity for ${row.code}`}
                              />
                              <button
                                type="button"
                                onClick={() => bumpQty(row.code, 1)}
                                className="w-6 h-7 flex items-center justify-center text-slate-600 hover:bg-slate-100"
                                aria-label={`Increase quantity for ${row.code}`}
                                data-testid={`asc-qty-inc-${row.code}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => onAdd(row)}
                              className="inline-flex items-center gap-1 px-2.5 h-7 rounded bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs font-bold transition-colors"
                              data-testid={`asc-add-${row.code}`}
                            >
                              <Plus className="w-3 h-3" /> Add{qty > 1 ? ` (${qty})` : ""}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-slate-500 italic">
            <span className="font-semibold">Hospital pricing</span> shown is the Sterile Mate authorized-distributor rate (per box of 200). +20% freight &amp; handling auto-applied at checkout unless you provide your own freight forwarder. Final pricing confirmed by Sterile Mate at PO — contact <a className="text-blue-700 underline" href="mailto:orders@sterilemate.com">orders@sterilemate.com</a> · <a className="text-blue-700 underline" href="tel:12814563458">281.456.3458</a>.
          </p>
        </div>
      )}
    </div>
  );
};

// ----- Pre-order form ---------------------------------------------------------
const PreOrderForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState({ name: "", email: "", facility: "", phone: "", products: "", quantities: "", needBy: "Q3" });
  const [submitting, setSubmitting] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Build a mailto payload — same architecture used elsewhere in the app
    const subject = encodeURIComponent(`American Surgical Pre-Order — ${data.facility || data.name}`);
    const body = encodeURIComponent(
      `Pre-order inquiry submitted via /marketplace/american-surgical\n\n` +
      `Contact:    ${data.name}\nEmail:      ${data.email}\nPhone:      ${data.phone}\nFacility:   ${data.facility}\n\n` +
      `Products:   ${data.products}\nQuantities: ${data.quantities}\nNeed by:    ${data.needBy}\n\n` +
      `Sterile Mate is an Authorized Distributor for American Surgical Company. ` +
      `Please contact me to confirm allotment and lock in pricing.`
    );
    window.location.href = `mailto:orders@sterilemate.com?subject=${subject}&body=${body}`;
    setTimeout(() => { setSubmitted(true); setSubmitting(false); }, 600);
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center" data-testid="preorder-thanks">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <h3 className="text-2xl font-bold text-emerald-900 mb-2">Pre-order request submitted</h3>
        <p className="text-emerald-800 mb-4">Outlook has been opened with your pre-order details. Send the email and our team will confirm allotment within 1 business day.</p>
        <Button onClick={() => setSubmitted(false)} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">Submit another</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="preorder-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Your Name</label>
          <input required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Dr. Jane Smith" data-testid="preorder-name" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Email</label>
          <input required type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="purchasing@hospital.org" data-testid="preorder-email" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Facility</label>
          <input required value={data.facility} onChange={(e) => setData({ ...data, facility: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Memorial Hermann" data-testid="preorder-facility" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Phone</label>
          <input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="(281) 555-0123" data-testid="preorder-phone" />
        </div>
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Products of interest</label>
        <textarea required rows={3} value={data.products} onChange={(e) => setData({ ...data, products: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Ray-Cot 60-09 (1/2 × 3), Delicot 63-04, Atlas Choice BIP-ATMI-04" data-testid="preorder-products" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Estimated quantities</label>
          <input value={data.quantities} onChange={(e) => setData({ ...data, quantities: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 50 boxes/month" data-testid="preorder-qty" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-700">Need by</label>
          <select value={data.needBy} onChange={(e) => setData({ ...data, needBy: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="preorder-need">
            <option>Immediate (rolling allotment)</option>
            <option>Q3 (Jul–Sep)</option>
            <option>Q4 (Oct–Dec)</option>
          </select>
        </div>
      </div>
      <Button type="submit" disabled={submitting} className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white text-base font-bold" data-testid="preorder-submit">
        {submitting ? "Sending…" : "Submit Pre-Order"} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
      <p className="text-xs text-slate-500 text-center">By submitting, your default email client will open with the pre-order details addressed to <strong>orders@sterilemate.com</strong>.</p>
    </form>
  );
};

// ----- Page -------------------------------------------------------------------
export default function AmericanSurgicalPage() {
  const { cart, setShowCart, addItem } = useQuoteCart();
  const [, setPriceTick] = useState(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    // Pull the latest retail prices from the admin catalog and re-render
    // when they arrive. If the fetch fails we silently keep the bootstrap
    // values that shipped with the JS bundle.
    refreshAscPricing().then((r) => { if (r?.ok) setPriceTick((n) => n + 1); });
  }, []);

  // FAQ block — mirrored 1:1 into FAQPage JSON-LD below so Google can show
  // these as "People Also Ask" cards.
  const ASC_FAQS = [
    {
      q: "Are American Surgical Ray-Cot® neuro patties latex-free?",
      a: "Yes. Every American Surgical Ray-Cot® and Delicot® neurosurgical pattie is 100% medical-grade rayon and latex-free. Latex is not used in the pattie, the locator string, the barium sulfate marker, or the packaging.",
    },
    {
      q: "What is the difference between Ray-Cot® and Delicot®?",
      a: "Both are American Surgical rayon neurosurgical patties. Ray-Cot® is the standard-thickness patty used in general neurosurgery, spine, and trauma — it delivers the manufacturer's headline 12% higher absorbency and 2× tensile strength claims. Delicot® is an ultra-thin microsurgical patty designed for micro-surgery, ENT, ophthalmology, and cranial-base procedures where minimum thickness is critical. Both share the same barium sulfate radiopaque line and locator string.",
    },
    {
      q: "Are Ray-Cot® neuro patties X-ray detectable?",
      a: "Yes. Every Ray-Cot® and Delicot® patty is manufactured with a single embedded barium sulfate radiopaque line. It is visible on intraoperative fluoroscopy and post-operative films — meeting Joint Commission NPSG.14 and AORN retained-surgical-item prevention requirements.",
    },
    {
      q: "Can I order Ray-Cot® neuro patties without a GPO contract?",
      a: "Yes. Sterile Mate is the authorized distributor for American Surgical Company and is non-GPO friendly. No Vizient, Premier, HealthTrust, or Intalere agreement is required. We offer net-30 terms on credit approval, certified small-business supplier-diversity credit on every invoice, no minimum order for established accounts, and freight-inclusive pricing on 5+ box orders in TX, LA, AL, and MS.",
    },
    {
      q: "How is a box of Ray-Cot® neuro patties packaged?",
      a: "Standard pack: 10 patties are wound on a count-verification card, sealed inside a sterile peel-open pouch, with 20 pouches per box — 200 patties per box. This is the U.S. industry standard, so case-cart pull sheets do not need to change when receiving American Surgical stock.",
    },
    {
      q: "Where are Ray-Cot® patties manufactured?",
      a: "American Surgical Company manufactures Ray-Cot®, Delicot®, and its full patty line in Salem, Massachusetts. The company has produced neurosurgical patties continuously since 1919 — one of the oldest continuously operating surgical-sponge manufacturers in the United States.",
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="american-surgical-page">
      <SEO
        title="American Surgical Ray-Cot® Neuro Patty Sponges — In Stock"
        description="Authorized distributor for American Surgical Ray-Cot® and Delicot® neurosurgical patty sponges (Salem, MA). In stock at Sterile Mate — Houston, TX. Serving TX / LA / AL / MS. Non-GPO friendly · certified small-business credit."
        canonical="/marketplace/american-surgical"
        keywords={[
          "American Surgical Ray-Cot neuro sponges in stock",
          "Ray-Cot neurosurgical pattie distributor",
          "Delicot microsurgical pattie",
          "neuro patty sponges in stock Texas",
          "neurosurgical cottonoids in stock",
        ]}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            productSchema({
              name: "American Surgical Ray-Cot® Neurosurgical Patty Sponges",
              sku: "RAY-COT",
              brand: "American Surgical Company",
              description: "Rayon neurosurgical patties with X-ray detectable locator string. 12% greater absorbency, 2x stronger than the leading competitor. Made in Salem, Massachusetts. Sterile Mate is the authorized distributor.",
              image: "https://www.americansurgical.com/wp-content/uploads/2023/06/Ray-Cot-NEW23-1024x707.png",
              url: "/marketplace/american-surgical",
              price: null,
              availability: "https://schema.org/InStock",
            }),
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: ASC_FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />
      {/* Floating Quote Cart launcher (visible everywhere on the page) */}
      <button
        onClick={() => setShowCart(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 h-12 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold shadow-xl shadow-amber-400/30 transition-all"
        data-testid="asc-cart-fab"
      >
        <ShoppingCart className="w-4 h-4" />
        Quote Cart {cart.length > 0 && <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-slate-900 text-amber-300 text-xs font-black px-1.5">{cart.length}</span>}
      </button>
      <SiteHeader />
      <MarketplaceCategoryNav currentCategory="american-surgical" />
      <MarketplaceBreadcrumb trail={[{ label: "American Surgical" }]} />
      {/* ============ HERO ============ */}
      <div className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-400/15 border border-amber-300/30 rounded-full mb-6">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-amber-200 text-xs font-bold uppercase tracking-widest">Now Authorized Distributor</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5 tracking-tight">
                Sterile Mate is now an<br />
                <span className="bg-gradient-to-r from-cyan-300 to-blue-200 bg-clip-text text-transparent">Authorized Distributor</span><br />
                for <span className="text-amber-300">American Surgical</span>
              </h1>
              <p className="text-lg text-blue-100 leading-relaxed mb-6 max-w-xl">
                Industry-leading pricing on the products that matter most when nationwide backorders strike — direct from the manufacturer in <strong>Salem, Massachusetts</strong>, no third-party markup.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#preorder" className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold transition-colors shadow-lg" data-testid="hero-preorder-cta">
                  Lock in Pre-Order Pricing <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/demo/american-surgical" className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-bold transition-colors shadow-lg" data-testid="hero-hospital-demo-cta">
                  Learn More <ArrowRight className="w-4 h-4" />
                </a>
                <a href={CATALOG_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition-colors" data-testid="hero-catalog-cta">
                  <Download className="w-4 h-4" /> Download 2026 Catalog
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl p-6 -rotate-2">
                <img src={ASC_PRODUCTS[0].img} alt="Ray-Cot Neurosurgical Pattie" className="w-full rounded-lg" />
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Made in U.S.A. · Salem, MA</span>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">39 Years</span>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 rotate-3 max-w-[200px]">
                <div className="flex items-center gap-1 mb-1">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                </div>
                <p className="text-xs text-slate-700 font-semibold">98% customer retention. Used every 4.2 seconds, worldwide.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ TRUST BAR ============ */}
      <div className="bg-slate-900 border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap justify-center gap-6 md:gap-12">
          {[
            { icon: ShieldCheck, text: "Authorized Distributor" },
            { icon: Award, text: "Made in U.S.A." },
            { icon: Building2, text: "Salem, MA — 39 Years" },
            { icon: Star, text: "98% Customer Retention" },
            { icon: Truck, text: "Q3 Allotment Open" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-slate-300">
              <item.icon className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ============ BACKORDER STORY ============ */}
      <section className="py-16 bg-amber-50/40 border-y border-amber-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
            <div className="w-14 h-14 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-md flex-shrink-0">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-3 tracking-tight">
                Nationwide Backorders Are Real. Our Answer Is Better.
              </h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                For months we&apos;ve been filling neurosurgical pattie orders through third-party distributors at premium pricing. <strong>That ends today.</strong>
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                As of this announcement, Sterile Mate is an <strong>Authorized Distributor for American Surgical Company</strong> — direct from the Salem, Massachusetts manufacturer. We&apos;re not on a GPO contract, but we will compete with their pricing and deliver from our allotment as it comes available for your immediate needs.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <Package className="w-5 h-5 text-amber-600 mb-2" />
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Direct from MFG</p>
                  <p className="text-sm text-slate-600">No third-party markup, no premium pricing.</p>
                </div>
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <Calendar className="w-5 h-5 text-amber-600 mb-2" />
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Q3 Allotment Open</p>
                  <p className="text-sm text-slate-600">Pre-orders for July–September are accepted now.</p>
                </div>
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <Truck className="w-5 h-5 text-amber-600 mb-2" />
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Rolling Fulfillment</p>
                  <p className="text-sm text-slate-600">As stock arrives, current-need orders ship first.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRODUCT WIDGETS ============ */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-700 mb-2">The Full American Surgical Catalog</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-3">
              {ASC_PRODUCTS.length} Product Lines · {ASC_PRODUCTS.reduce((n, p) => n + p.skuGroups.reduce((m, g) => m + g.rows.length, 0), 0)}+ SKUs
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Every product made in Salem, Massachusetts. Click <strong>View SKUs</strong> on any widget to expand the full SKU table and add items directly to your Sterile Mate Quote Cart.
            </p>
          </div>
          <div className="space-y-6">
            {ASC_PRODUCTS.map((product) => (
              <ProductWidget key={product.id} product={product} addItem={addItem} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ FLIPBOOK CATALOG ============ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-700 mb-2">2026 Sterile Mate × American Surgical Catalog</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-3">
              Browse the Full Catalog
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">All 100+ SKUs, sizes, packaging, and product photography — flip through like a real brochure.</p>
          </div>

          <PdfFlipbook
            fileUrl={CATALOG_URL}
            title="2026 American Surgical Catalog"
            downloadName={CATALOG_DOWNLOAD_NAME}
          />
        </div>
      </section>

      {/* ============ PRE-ORDER ============ */}
      <section id="preorder" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full mb-4">
              <Calendar className="w-3.5 h-3.5 text-blue-700" />
              <span className="text-blue-800 text-xs font-bold uppercase tracking-wider">Q3 Allotment — Open Now</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-3">
              Lock In Your Pre-Order
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              We need firm commitments to allocate our Q3 allotment. Submit your pre-order today and we&apos;ll confirm your pricing and lead time within one business day.
            </p>
          </div>
          <PreOrderForm />
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-16 bg-white border-t border-slate-100" data-testid="asc-faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-700 mb-2">Buyer FAQ</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-3">
              Ray-Cot<sup>®</sup> neuro pattie — the questions we get most
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Six answers hospital procurement asks before submitting a PO. For a full clinical primer see{" "}
              <Link to="/resources/what-are-neuro-patty-sponges" className="text-blue-700 font-semibold underline">
                What Are Neuro Patty Sponges?
              </Link>
            </p>
          </div>
          <div className="space-y-3">
            {ASC_FAQS.map((f, i) => (
              <details
                key={f.q}
                className="group border border-slate-200 rounded-lg bg-white overflow-hidden"
                data-testid={`asc-faq-${i}`}
              >
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-slate-50">
                  <span className="font-semibold text-slate-900">{f.q}</span>
                  <span className="text-slate-400 group-open:rotate-90 transition-transform">→</span>
                </summary>
                <div className="px-5 pb-5 text-slate-700 leading-relaxed border-t border-slate-100 pt-3">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA FOOTER ============ */}
      <section className="py-14 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-4">Talk to a Sterile Mate Specialist Today</h2>
          <p className="text-blue-100 max-w-xl mx-auto mb-6">Questions on sizing, allotment, or contract pricing? Our team will walk you through the catalog and finalize your order.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="mailto:orders@sterilemate.com?subject=American%20Surgical%20Inquiry" className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold" data-testid="cta-email">
              <Mail className="w-4 h-4" /> orders@sterilemate.com
            </a>
            <a href="tel:12814563458" className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold" data-testid="cta-phone">
              <Phone className="w-4 h-4" /> 281.456.3458
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/static/facility-supplies/fabco-neuro-sponge-spec-sheet.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold"
              data-testid="cta-fabco-spec-sheet"
            >
              <FileText className="w-4 h-4" /> Fabco-Equivalent Spec Sheet
            </a>
            <Link to="/sterilemate/surgical" className="inline-flex items-center gap-2 px-6 h-12 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold" data-testid="cta-back-surgical">
              <FileText className="w-4 h-4" /> Surgical Marketplace
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
